import { lumi } from '@/lib/lumi'
import { useState } from 'react'

export interface CalculatorInput {
  length: number
  width: number
  thickness: number
  wasteFactor: number
}

export interface CalculatorResult {
  cubicFeet: number
  cubicYards: number
  bags80lb: number
  bags60lb: number
}

export interface CalculatorHistory {
  _id: string
  calculationType: 'slab' | 'fence_post' | 'block_wall' | 'sonotube' | 'steps' | 'weight' | 'cost'
  projectName?: string
  inputs: CalculatorInput
  results: CalculatorResult
  creator: string
  createdAt: string
  updatedAt: string
}

export function useCalculatorHistory() {
  const [history, setHistory] = useState<CalculatorHistory[]>([])
  const [loading, setLoading] = useState(false)

  const fetchHistory = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.calculator_history.list({
        sort: { createdAt: -1 },
        limit: 10
      })
      setHistory(list)
      console.log('Calculator history fetched:', list)
    } catch (error) {
      console.error('Failed to fetch history:', error)
    } finally {
      setLoading(false)
    }
  }

  const saveCalculation = async (data: {
    calculationType: 'slab' | 'fence_post' | 'block_wall' | 'sonotube' | 'steps' | 'weight' | 'cost'
    projectName?: string
    inputs: CalculatorInput
    results: CalculatorResult
  }) => {
    try {
      const user = lumi.auth.user
      const now = new Date().toISOString()

      const record = await lumi.entities.calculator_history.create({
        calculationType: data.calculationType,
        projectName: data.projectName || '',
        inputs: data.inputs,
        results: data.results,
        creator: user?.userId || 'anonymous',
        createdAt: now,
        updatedAt: now
      })

      console.log('Calculation saved:', record)
      await fetchHistory()
      return record
    } catch (error) {
      console.error('Failed to save calculation:', error)
      throw error
    }
  }

  const deleteCalculation = async (id: string) => {
    if (typeof id !== 'string' || id === '[object Object]') {
      console.error('Invalid ID: must be a string')
      return
    }

    try {
      await lumi.entities.calculator_history.delete(id)
      console.log('Calculation deleted:', id)
      await fetchHistory()
    } catch (error) {
      console.error('Failed to delete calculation:', error)
      throw error
    }
  }

  return {
    history,
    loading,
    fetchHistory,
    saveCalculation,
    deleteCalculation
  }
}
